<template>
  <div v-if="siteKey" ref="container" class="component-turnstile"></div>
</template>

<script>
// Cloudflare Turnstile, almost always invisible ("interaction-only"): it only shows a box when Cloudflare
// is unsure about the visitor. Without VUE_APP_TURNSTILE_SITE_KEY nothing renders and the token stays empty.
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let scriptPromise = null;

function loadScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = (error) => {
        scriptPromise = null; // let the next mount try again
        reject(error);
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

export default {
  name: "ComponentTurnstile",
  emits: ["token"],
  data() {
    return {
      widgetId: null,
      siteKey: process.env.VUE_APP_TURNSTILE_SITE_KEY || "",
      current: "",
      waiters: [],
    };
  },
  mounted() {
    if (!this.siteKey) return;
    loadScript()
      .then((turnstile) => {
        if (!this.$refs.container) return;
        this.widgetId = turnstile.render(this.$refs.container, {
          sitekey: this.siteKey,
          appearance: "interaction-only",
          language: "pt-br",
          callback: (token) => this.setToken(token),
          "expired-callback": () => this.setToken(""),
          "error-callback": () => this.setToken(""),
        });
      })
      .catch(() => {}); // blocked script: the server lets people through when it cannot check
  },
  beforeUnmount() {
    if (this.widgetId !== null && window.turnstile) window.turnstile.remove(this.widgetId);
    this.flush("");
  },
  methods: {
    setToken(token) {
      this.current = token || "";
      this.$emit("token", this.current);
      if (this.current) this.flush(this.current);
    },
    flush(token) {
      this.waiters.splice(0).forEach((resolve) => resolve(token));
    },
    // Tokens are single use: call after each submit. The spent token is dropped at once,
    // so a quick second submit waits for the new one (token()) instead of sending the old.
    reset() {
      this.setToken("");
      if (this.widgetId !== null && window.turnstile) window.turnstile.reset(this.widgetId);
    },
    // The token to send now: the current one, or the next one Cloudflare issues (up to `ms`).
    // No widget (no key, script blocked): "" right away, and the server decides.
    token(ms = 8000) {
      if (this.current || this.widgetId === null) return Promise.resolve(this.current);
      return new Promise((resolve) => {
        this.waiters.push(resolve);
        setTimeout(() => {
          const i = this.waiters.indexOf(resolve);
          if (i >= 0) {
            this.waiters.splice(i, 1);
            resolve(this.current);
          }
        }, ms);
      });
    },
  },
};
</script>

<style scoped>
.component-turnstile {
  display: flex;
  justify-content: center;
  margin: 0.5em 0;
}
</style>
